/**
 * Centralized status color utility for consistent status chip styling across the app
 * Follows the pattern: light bg + dark text in light mode, dark bg + light text in dark mode
 */

export type StatusType =
  | "CREATED"
  | "SENT"
  | "APPROVED"
  | "DECLINED"
  | "DRAFT"
  | "PAID"
  | "VOIDED"
  | "DELETED"
  | "SUBMITTED"
  | "PENDING"
  | "COMPLETED"
  | "IN_PROGRESS"
  | "NOT_STARTED"
  | "BLOCKED"
  | "REJECTED"
  | "CANCELLED"
  | "REQUESTED";

/**
 * Get consistent status chip colors for quotes
 */
export const getQuoteStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    CREATED: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
    SENT: "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    APPROVED:
      "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    DECLINED: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
  };
  return (
    colors[status] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent status chip colors for invoices
 */
export const getInvoiceStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    DRAFT: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
    APPROVED:
      "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    SUBMITTED:
      "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    PAID: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    VOIDED: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
    DELETED: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
  };
  return (
    colors[status] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent status chip colors for bills
 */
export const getBillStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    DRAFT: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
    APPROVED:
      "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    SUBMITTED:
      "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    PAID: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    VOIDED: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
    DELETED: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
  };
  return (
    colors[status] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent status chip colors for timesheets
 */
export const getTimesheetStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    draft: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
    submitted:
      "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    approved:
      "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    rejected: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
  };
  return (
    colors[status.toLowerCase()] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent status chip colors for tasks
 */
export const getTaskStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    NOT_STARTED: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
    IN_PROGRESS:
      "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    COMPLETED:
      "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    BLOCKED: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
  };
  return (
    colors[status] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent status chip colors for leave requests
 */
export const getLeaveStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    pending:
      "bg-warning-100 dark:bg-warning-800 text-warning-700 dark:text-warning-300",
    requested:
      "bg-warning-100 dark:bg-warning-800 text-warning-700 dark:text-warning-300",
    approved:
      "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    completed:
      "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300",
    declined: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
    cancelled: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
  };
  return (
    colors[status.toLowerCase()] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent status chip colors for project created badge
 */
export const getProjectCreatedColor = (): string => {
  return "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300";
};

/**
 * Get consistent hover color for project created badge
 */
export const getProjectCreatedHoverColor = (): string => {
  return "hover:bg-purple-200 dark:hover:bg-purple-700";
};

/**
 * Get consistent status chip colors for project status (active/inactive/archived)
 */
export const getProjectStatusColor = (status: "active" | "inactive" | "archived"): string => {
  const colors: Record<string, string> = {
    active: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    inactive: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
    archived: "bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300",
  };
  return colors[status] || colors.inactive;
};

/**
 * Get consistent status chip colors for user/client portal status
 */
export const getUserStatusColor = (status: "accepted" | "pending" | string): string => {
  const colors: Record<string, string> = {
    accepted: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    pending: "bg-warning-100 dark:bg-warning-800 text-warning-700 dark:text-warning-300",
  };
  return colors[status.toLowerCase()] || colors.pending;
};

/**
 * Get consistent priority chip colors for tasks
 */
export const getPriorityColor = (priority: "HIGH" | "MEDIUM" | "LOW" | string): string => {
  const colors: Record<string, string> = {
    HIGH: "bg-error-100 dark:bg-error-800 text-error-800 dark:text-error-300",
    MEDIUM: "bg-warning-100 dark:bg-warning-800 text-warning-900 dark:text-warning-300",
    LOW: "bg-success-100 dark:bg-success-800 text-success-800 dark:text-success-300",
  };
  return colors[priority.toUpperCase()] || "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent contact type chip colors
 */
export const getContactTypeColor = (type: "client" | "supplier" | "subContractor" | "contact"): string => {
  const colors: Record<string, string> = {
    client: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    supplier: "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300",
    subContractor: "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    contact: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
  };
  return colors[type] || colors.contact;
};

/**
 * Get consistent staff status chip colors (active/inactive)
 */
export const getStaffStatusColor = (isActive: boolean): string => {
  return isActive
    ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent payroll integration badge colors
 * Matches the app-level pattern used in Contacts component
 */
export const getPayrollIntegrationColor = (integration: "xero" | "myob"): string => {
  const colors: Record<string, string> = {
    xero: "bg-[#cffafe] text-[#0e7490] dark:bg-[#164e63] dark:text-[#67e8f9]",
    myob: "bg-[#fee2e2] text-[#991b1b] dark:bg-[#7f1d1d] dark:text-[#fecaca]",
  };
  return colors[integration.toLowerCase()] || colors.xero;
};

/**
 * Get consistent notification card background colors
 * App-level styling for notification card views
 * Follows the pattern: bg-{color}-50 dark:bg-{color}-900/20
 */
export const getNotificationCardBg = (type: "info" | "success" | "warning" | "error"): string => {
  const colors: Record<string, string> = {
    info: "bg-blue-50 dark:bg-blue-900/20",
    success: "bg-green-50 dark:bg-green-900/20",
    warning: "bg-yellow-50 dark:bg-yellow-900/20",
    error: "bg-red-50 dark:bg-red-900/20",
  };
  return colors[type] || colors.info;
};

/**
 * Get consistent notification card heading text colors
 * App-level styling for notification card headings
 * Follows the pattern: text-{color}-700 dark:text-{color}-400
 */
export const getNotificationCardHeading = (type: "info" | "success" | "warning" | "error"): string => {
  const colors: Record<string, string> = {
    info: "text-blue-700 dark:text-blue-400",
    success: "text-green-700 dark:text-green-400",
    warning: "text-yellow-700 dark:text-yellow-400",
    error: "text-red-700 dark:text-red-400",
  };
  return colors[type] || colors.info;
};

/**
 * Get consistent notification card body text colors
 * App-level styling for notification card body text
 * Follows the pattern: text-{color}-900 dark:text-{color}-100
 */
export const getNotificationCardText = (type: "info" | "success" | "warning" | "error"): string => {
  const colors: Record<string, string> = {
    info: "text-blue-900 dark:text-blue-100",
    success: "text-green-900 dark:text-green-100",
    warning: "text-yellow-900 dark:text-yellow-100",
    error: "text-red-900 dark:text-red-100",
  };
  return colors[type] || colors.info;
};

/**
 * Get consistent notification card border colors
 * App-level styling for notification card borders
 */
export const getNotificationCardBorder = (type: "info" | "success" | "warning" | "error"): string => {
  const colors: Record<string, string> = {
    info: "border-blue-300 dark:border-blue-700/50",
    success: "border-green-300 dark:border-green-700/50",
    warning: "border-yellow-300 dark:border-yellow-700/50",
    error: "border-red-300 dark:border-red-700/50",
  };
  return colors[type] || colors.info;
};

/**
 * Get consistent notification type badge/chip colors
 * App-level styling for notification type badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getNotificationTypeBadge = (type: "info" | "success" | "warning" | "error"): string => {
  const colors: Record<string, string> = {
    info: "bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300",
    success: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    warning: "bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300",
    error: "bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300",
  };
  return colors[type] || colors.info;
};

/**
 * Get consistent notification read status chip colors
 * App-level styling for read/unread status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getNotificationReadStatus = (isRead: boolean): string => {
  return isRead
    ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent user status chip colors (active/inactive/pending)
 * App-level styling for user status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getUserManagementStatus = (isPending: boolean, isActive: boolean): string => {
  if (isPending) {
    return "bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300";
  }
  return isActive
    ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent chart of accounts class chip colors
 * App-level styling for account class badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getAccountClassColor = (accountClass?: string): string => {
  const colors: Record<string, string> = {
    ASSET: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    LIABILITY: "bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300",
    EQUITY: "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300",
    REVENUE: "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    EXPENSE: "bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300",
  };
  return (
    colors[accountClass || ""] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent chart of accounts type chip colors
 * App-level styling for account type badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getAccountTypeColor = (): string => {
  return "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent chart of accounts status chip colors
 * App-level styling for account status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getAccountStatusColor = (isActive: boolean, status: string): string => {
  if (isActive && status === "ACTIVE") {
    return "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300";
  }
  return "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent tax rate status chip colors
 * App-level styling for tax rate status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getTaxRateStatusColor = (isActive: boolean, status: string): string => {
  if (isActive && status === "ACTIVE") {
    return "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300";
  }
  return "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent payroll calendar type chip colors
 * App-level styling for calendar type badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getPayrollCalendarTypeColor = (): string => {
  return "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300";
};

/**
 * Get consistent payroll calendar status chip colors
 * App-level styling for calendar status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getPayrollCalendarStatusColor = (isActive: boolean): string => {
  return isActive
    ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent leave type units chip colors
 * App-level styling for leave units badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getLeaveTypeUnitsColor = (): string => {
  return "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300";
};

/**
 * Get consistent leave type paid/unpaid chip colors
 * App-level styling for leave type badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getLeaveTypePaidColor = (isPaid: boolean): string => {
  return isPaid
    ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent leave type status chip colors
 * App-level styling for leave type status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getLeaveTypeStatusColor = (isActive: boolean): string => {
  return isActive
    ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent leave type can request chip colors
 * App-level styling for can request badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getLeaveTypeCanRequestColor = (canBeRequested: boolean): string => {
  return canBeRequested
    ? "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent earnings rate type chip colors
 * App-level styling for earnings type badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getEarningsRateTypeColor = (earningsType: string): string => {
  const colors: Record<string, string> = {
    overtime_earnings: "bg-warning-100 dark:bg-warning-800 text-warning-700 dark:text-warning-300",
    allowance: "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300",
    bonus: "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300",
    commission: "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300",
    ordinary_earnings: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    other: "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300",
  };
  return (
    colors[earningsType] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};

/**
 * Get consistent earnings rate status chip colors
 * App-level styling for earnings rate status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getEarningsRateStatusColor = (isActive: boolean): string => {
  return isActive
    ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300"
    : "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent tracking code status chip colors
 * App-level styling for tracking code status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getTrackingCodeStatusColor = (status: string): string => {
  // Most tracking codes are ACTIVE, but handle other statuses if needed
  if (status === "ACTIVE" || status === "active") {
    return "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300";
  }
  return "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300";
};

/**
 * Get consistent payment release status chip colors
 * App-level styling for payment release status badges
 * Follows the pattern: bg-{color}-100 dark:bg-{color}-800 text-{color}-700 dark:text-{color}-300
 */
export const getPaymentReleaseStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    APPROVED: "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300",
    REJECTED: "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300",
    PENDING: "bg-warning-100 dark:bg-warning-800 text-warning-700 dark:text-warning-300",
  };
  return (
    colors[status] ||
    "bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300"
  );
};
